import { z } from "zod";
/**
 * The live coin balance — phase plan **S4-8**.
 *
 * ── One channel per player, and that IS the isolation ───────────────────────
 *
 * A player's balance is theirs and nobody else's. Rather than fanning one
 * "balances changed" channel out to every socket and filtering per client — a
 * filter is a line of code that can be wrong, and when it is wrong it leaks a
 * stranger's wallet — the channel name **contains the player id**, and a room
 * subscribes a socket to exactly one of them: the one belonging to the identity
 * `onAuth` bound to that socket. Player A's socket is never subscribed to B's
 * channel, so there is no filter to get wrong.
 *
 * ── Redis pub/sub, and LOCKED rule 25 is about Postgres ─────────────────────
 *
 * Rule 25 forbids a **second `LISTEN` connection to Postgres**; there is exactly
 * one in the system and it lives in the api. Redis pub/sub is the sanctioned bus
 * (§16e A22 is explicit that a second *publisher* is fine, a second Postgres
 * listener is not), and this rides it — the same presence Redis the lobby
 * channel already uses.
 */
/** `wr:balance:v1:<players.id>`. Versioned, because the payload may grow. */
export const BALANCE_CHANNEL_PREFIX = "wr:balance:v1:";
export function balanceChannel(playerId) {
    return `${BALANCE_CHANNEL_PREFIX}${playerId}`;
}
export const balanceEventSchema = z.object({
    /**
     * Whose balance this is.
     *
     * It is on the payload as well as in the channel name so a relay can assert
     * that what it received matches the subscription it holds — a belt-and-braces
     * check against a mis-wired subscription ever putting one player's number on
     * another's screen. It does **not** reach the browser: the room strips it, and
     * §7 of the lobby spec is standing ("a player's id never reaches another
     * player's screen") — here it would be the *viewer's own* id, which is no
     * safer to start shipping for no reason.
     */
    playerId: z.string().uuid(),
    /**
     * `coin_transactions.balance_after`, **as computed inside the transaction that
     * wrote it** — never recomputed, never a client-side sum.
     *
     * This is the one-truth rule (S3 §9.1) extended over the wire: the number on
     * the chip, the number in the ledger row and the number the results screen
     * renders are one value with three renderings.
     */
    balanceCoins: z.number().int().min(0),
    /** `coin_transactions.reason`, so the chip can animate a credit differently. */
    reason: z.string(),
    /** The row that caused it. A client that sees the same id twice ignores the second. */
    transactionId: z.number().int(),
    at: z.string(),
});
/**
 * The message name, shared so `apps/game` and `apps/web` cannot disagree about
 * it — a hand-typed `"balance"` on one side is a silently dead listener on the
 * other, which is the same class of defect the lobby channel name moved here to
 * avoid (T-188). The player id is dropped from every payload sent under it (see
 * the note on `playerId` above).
 */
export const BALANCE_MESSAGE = "balance";
/**
 * A snapshot's stand-in for `coin_transactions.reason`.
 *
 * A snapshot is not caused by a transaction, so it has no reason — and inventing
 * a plausible-looking one (`room.entry`, say) would let a client animate a debit
 * that did not just happen. This value is deliberately not a member of the
 * `coin_transaction_reason` enum: a consumer that switches on reasons falls
 * through, which is the correct behaviour for a value that is not a ledger fact.
 */
export const BALANCE_SNAPSHOT_REASON = "snapshot";
/**
 * What the room forwards to the browser over the socket.
 *
 * ── `snapshot` — §16g **A76** ────────────────────────────────────────────────
 *
 * > *"On `onJoin` and on `onReconnect`, the room sends that client one balance
 * > message carrying their current `player_balances.balance`, marked as a
 * > snapshot."*
 *
 * It is on the **message** and deliberately not on `balanceEvent`: a published
 * event is always caused by a ledger write, and a snapshot is never published —
 * it is sent to exactly one socket, by the room that socket is attached to. The
 * two shapes have different provenance, so they have different types.
 *
 * A client adopts a snapshot under Ivy §9.4's **reconcile** rules — instant
 * swap, no cross-fade, no rise, no coin-dot pulse — because it is a value the
 * player did not witness arriving. `transactionId` carries the id of the last
 * transaction the balance includes (0 for a player who has never transacted), so
 * the client's existing ordering watermark works unchanged and a snapshot can
 * never roll a newer live event back.
 */
export const balanceMessageSchema = balanceEventSchema
    .omit({ playerId: true })
    .extend({ snapshot: z.literal(true).optional() });
//# sourceMappingURL=balance.js.map